import type { WebviewWindow } from "@tauri-apps/api/webviewWindow"

import { Channel, convertFileSrc, invoke } from "@tauri-apps/api/core"
import { listen, TauriEvent } from "@tauri-apps/api/event"
import { getCurrentWebviewWindow } from "@tauri-apps/api/webviewWindow"
import { open } from "@tauri-apps/plugin-dialog"
import { type } from "@tauri-apps/plugin-os"

import type { CacheState } from "~/models/polycache/CacheState"

interface State {
  window?: WebviewWindow
  isWindowMaximized: boolean
  platform?: "linux" | "windows" | "macos" | "ios" | "android"
  handlersInitialized: boolean
}
let tauriState: Ref<State>

function defaultState() {
  return {
    isWindowMaximized: false,
    handlersInitialized: false,
  }
}
function clearTauriState() {
  tauriState.value = defaultState()
}
function errorIfNotOnDesktop() {
  if (!isDesktop())
    throw new Error("Calling tauri methods on browser")
}

async function initTauriHandlers() {
  if (!isDesktop())
    return
  if (tauriState.value.handlersInitialized)
    return console.debug("Tauri handlers already initialized, skipping")
  tauriState.value.handlersInitialized = true

  tauriState.value.platform = await type()
  const current: WebviewWindow = await getCurrentWebviewWindow()
  if (!current)
    return console.error("No window")
  tauriState.value.window = current
  current.onResized(async (_event) => {
    const isMaximized = await current.isMaximized()
    if (isDefined(isMaximized))
      tauriState.value.isWindowMaximized = isMaximized
  })
}

async function minimizeWindow() {
  errorIfNotOnDesktop()
  const window = tauriState.value.window
  if (!window)
    return console.error("No window")
  window.minimize()
}

async function maximizeWindow() {
  errorIfNotOnDesktop()
  const window = tauriState.value.window
  if (!window)
    return console.error("No window")
  window.maximize()
}

async function unmaximizeWindow() {
  errorIfNotOnDesktop()
  const window = tauriState.value.window
  if (!window)
    return console.error("No window")
  window.unmaximize()
}

async function toggleFullscreenWindow() {
  errorIfNotOnDesktop()
  const window = tauriState.value.window
  if (!window)
    return console.error("No window")
  window.setFullscreen(!window.isFullscreen())
}

async function closeWindow() {
  errorIfNotOnDesktop()
  const window = tauriState.value.window
  if (!window)
    return console.error("No window")
  window.close()
}

async function login(request: LoginRequest) {
  errorIfNotOnDesktop()
  return invoke<LoginResponse>("login", { request })
}

async function signup(request: SignupRequest) {
  errorIfNotOnDesktop()
  return invoke<SignupResponse>("signup", { request })
}

async function verify(request: VerifyRequest) {
  errorIfNotOnDesktop()
  return invoke<LoginResponse>("verify", { request })
}

async function resendVerification(request: NewVerificationCodeRequest) {
  errorIfNotOnDesktop()
  return invoke<LoginResponse>("resend_verification", { request })
}

async function forgotPassword(request: ForgotPasswordRequest) {
  errorIfNotOnDesktop()
  return invoke<LoginResponse>("forgot_password", { request })
}

async function changePassword(request: ChangePasswordRequest) {
  errorIfNotOnDesktop()
  return invoke<LoginResponse>("change_password", { request })
}

async function createNewWindow() {
  errorIfNotOnDesktop()
  console.debug("creating new windows")
  return invoke("create_new_window")
}

async function postDatagramBuf(msg: Uint8Array) {
  errorIfNotOnDesktop()
  return invoke("post_datagram_buf", msg)
}

async function postDatagramBufOffline(msg: Uint8Array) {
  errorIfNotOnDesktop()
  return invoke("post_datagram_buf_offline", msg)
}

async function getFileOfflineStatus(checksum: Uint8Array): Promise<string | null> {
  errorIfNotOnDesktop()
  return invoke("get_file_offline_status", { checksum })
}

async function downloadFileToSandbox(checksum: Uint8Array, presignedUrl: string): Promise<string> {
  errorIfNotOnDesktop()
  return invoke("download_file_to_sandbox", { checksum, presignedUrl })
}

async function loadFileAsTranscodedPng(sandboxPath: string): Promise<Uint8Array> {
  errorIfNotOnDesktop()
  return invoke("load_thumbnail", { sandboxPath })
}

async function saveThumbnail(bytes: Uint8Array, thumbnailUri: string): Promise<string[]> {
  errorIfNotOnDesktop()
  return invoke("save_thumbnail", bytes, { headers: { thumbnailUri } })
}
async function loadThumbnail(thumbnailUri: string): Promise<Uint8Array> {
  errorIfNotOnDesktop()
  return invoke("load_thumbnail", { thumbnailUri })
}

async function getThumbnailOfflineStatus(thumbnailUris: string[]): Promise<string[]> {
  errorIfNotOnDesktop()
  return invoke("get_thumbnail_offline_status", { thumbnailUris })
}

async function getThumbnailWebFriendly(thumbnailUri: string): Promise<string> {
  errorIfNotOnDesktop()
  const uri = await invoke("get_thumbnail_web_friendly", { thumbnailUri })
  const webFriendlyUri = convertFileSrc(uri as string)
  return webFriendlyUri
}

/** Converts the output of our sandbox method into a web-usable url, no dataUri needed! */
async function getSandboxPathWebFriendly(sandboxPath: string): Promise<string> {
  errorIfNotOnDesktop()
  const webFriendlyUri = convertFileSrc(sandboxPath)
  return webFriendlyUri
}

async function addTransferCallback(callback: (base64string: string) => void) {
  errorIfNotOnDesktop()
  await listen<string>("transfer-message", ({ payload }) => callback(payload))
}

/**
 * TODO: This currently still uses JSON to send messages
 * back and forth
 * @param callback The method that receives messages from the cache
 */
async function createDatagramChannel(callback: (datagramBufBytes: Uint8Array) => void): Promise<CacheState> {
  errorIfNotOnDesktop()
  const onDatagramBuf = new Channel<ArrayBuffer>()
  onDatagramBuf.onmessage = datagram => callback(new Uint8Array(datagram))
  return invoke("create_datagram_channel", { onDatagramBuf })
}

/** @returns un-listen function to deregister listen event */
async function registerDragAndDropEvent() {
  const { onDesktopFilesDropped } = useJobs()
  errorIfNotOnDesktop()
  return await listen<{ paths: string[] }>(
    TauriEvent.DRAG_DROP,
    event => onDesktopFilesDropped(event.payload.paths),
  )
}

async function uploadItems(
  paths: string[],
  userId: string,
  folderId: string,
  transferId: string,
) {
  console.debug("Uploading", paths, userId, folderId, transferId)

  return invoke("upload_items", { paths, userId, folderId, transferId })
}

async function downloadFiles({ transferId, bytes }: {
  transferId: string
  bytes: Uint8Array
}): Promise<SandboxDownloadFile[]> {
  return invoke("download_items", bytes, { headers: { transferId } })
}

async function copyFiles({ transferId, userId, folderId, bytes }: {
  transferId: string
  userId: string
  folderId: string
  bytes: Uint8Array
}) {
  return invoke("coy_items", bytes, { headers: { transferId, userId, folderId } })
}

async function launchWithDefaultApplication(path: string) {
  return invoke("launch_with_default_application", { path })
}

async function sendUndoCommand() {
  return invoke("undo_last_action")
}

async function sendRedoCommand() {
  return invoke("redo_last_action")
}

async function createSharedFolderWithDefaultProperties(
  sharedFolderPreHash: string,
  homePreHash: string,
  archivePreHash: string,
  ownerId: string,
  name: string,
  handle: string,
) {
  return invoke("create_shared_folder_with_default_properties", {
    sharedFolderPreHash,
    homePreHash,
    archivePreHash,
    ownerId,
    name,
    handle,
  })
}

async function createOnboardingContent() {
  errorIfNotOnDesktop()
  return invoke("create_onboarding_content")
}

async function uploadFromDesktopFilePicker(folder?: InterfaceFile) {
  const { onDesktopFilesDropped } = useJobs()
  const paths = await open({
    multiple: true,
    filters: [],
  })
  if (!paths) {
    return console.warn("No files selected")
  }
  return await onDesktopFilesDropped(paths, folder)
}

async function findMatchingConversations(query: string): Promise<string[]> {
  errorIfNotOnDesktop()
  const conversations = await invoke("find_matching_conversations", { query })
  return conversations as string[]
}

export default function () {
  tauriState = useState("tauri-data", defaultState)

  return {
    tauriState,
    clearTauriState,
    initTauriHandlers,
    minimizeWindow,
    maximizeWindow,
    unmaximizeWindow,
    toggleFullscreenWindow,
    closeWindow,
    login,
    signup,
    verify,
    resendVerification,
    forgotPassword,
    changePassword,
    postDatagramBuf,
    postDatagramBufOffline,
    createDatagramChannel,
    addTransferCallback,
    registerDragAndDropEvent,
    getThumbnailWebFriendly,
    getSandboxPathWebFriendly,
    createNewWindow,
    uploadItems,
    downloadFiles,
    copyFiles,
    getFileOfflineStatus,
    loadFileAsTranscodedPng,
    saveThumbnail,
    loadThumbnail,
    getThumbnailOfflineStatus,
    launchWithDefaultApplication,
    downloadFileToSandbox,
    createSharedFolderWithDefaultProperties,
    sendUndoCommand,
    sendRedoCommand,
    uploadFromDesktopFilePicker,
    findMatchingConversations,
    createOnboardingContent,
  }
}
